/**
 * Tabs + split groups. One model powers both (the AFFiNE "View" lesson):
 * the center area holds 1..n horizontal groups; each group holds tabs.
 * A tab points at a workspace item (note or folder).
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { newId } from "@hydra-notes/shared";

export interface Tab {
  id: string;
  itemId: string;
}

export interface TabGroup {
  id: string;
  tabs: Tab[];
  activeTabId: string | null;
}

interface TabsState {
  groups: TabGroup[];
  activeGroupId: string;

  openItem: (itemId: string, opts?: { newTab?: boolean; groupId?: string }) => void;
  closeTab: (groupId: string, tabId: string) => void;
  closeItemEverywhere: (itemIds: string[]) => void;
  activateTab: (groupId: string, tabId: string) => void;
  setActiveGroup: (groupId: string) => void;
  moveTab: (fromGroup: string, tabId: string, toGroup: string, toIndex: number) => void;
  splitRight: (groupId: string, tabId: string) => void;
  activeTab: () => { group: TabGroup; tab: Tab } | null;
  cycleTab: (dir: 1 | -1) => void;
}

function makeGroup(): TabGroup {
  return { id: newId(), tabs: [], activeTabId: null };
}

const initialGroup = makeGroup();

export const useTabs = create<TabsState>()(
  persist(
    (set, get) => ({
      groups: [initialGroup],
      activeGroupId: initialGroup.id,

      openItem: (itemId, opts) => {
        set((s) => {
          const groupId = opts?.groupId ?? s.activeGroupId;
          const groups = s.groups.map((g) => {
            if (g.id !== groupId) return g;
            // reuse an existing tab for this item
            const existing = g.tabs.find((t) => t.itemId === itemId);
            if (existing && !opts?.newTab) return { ...g, activeTabId: existing.id };
            if (existing && opts?.newTab) return { ...g, activeTabId: existing.id };
            const tab: Tab = { id: newId(), itemId };
            if (opts?.newTab || g.tabs.length === 0) {
              return { ...g, tabs: [...g.tabs, tab], activeTabId: tab.id };
            }
            // replace content of the active tab (single-click open behavior)
            const tabs = g.tabs.map((t) => (t.id === g.activeTabId ? { ...t, itemId } : t));
            return { ...g, tabs, activeTabId: g.activeTabId };
          });
          return { groups, activeGroupId: groupId };
        });
      },

      closeTab: (groupId, tabId) => {
        set((s) => {
          let groups = s.groups.map((g) => {
            if (g.id !== groupId) return g;
            const idx = g.tabs.findIndex((t) => t.id === tabId);
            const tabs = g.tabs.filter((t) => t.id !== tabId);
            const activeTabId =
              g.activeTabId === tabId ? (tabs[Math.min(idx, tabs.length - 1)]?.id ?? null) : g.activeTabId;
            return { ...g, tabs, activeTabId };
          });
          // drop empty groups (but always keep one)
          if (groups.length > 1) groups = groups.filter((g) => g.tabs.length > 0);
          if (groups.length === 0) groups = [makeGroup()];
          const activeGroupId = groups.some((g) => g.id === s.activeGroupId) ? s.activeGroupId : groups[0]!.id;
          return { groups, activeGroupId };
        });
      },

      closeItemEverywhere: (itemIds) => {
        const idSet = new Set(itemIds);
        set((s) => {
          let groups = s.groups.map((g) => {
            const tabs = g.tabs.filter((t) => !idSet.has(t.itemId));
            const activeTabId = tabs.some((t) => t.id === g.activeTabId)
              ? g.activeTabId
              : (tabs[tabs.length - 1]?.id ?? null);
            return { ...g, tabs, activeTabId };
          });
          if (groups.length > 1) groups = groups.filter((g) => g.tabs.length > 0);
          if (groups.length === 0) groups = [makeGroup()];
          const activeGroupId = groups.some((g) => g.id === s.activeGroupId) ? s.activeGroupId : groups[0]!.id;
          return { groups, activeGroupId };
        });
      },

      activateTab: (groupId, tabId) => {
        set((s) => ({
          activeGroupId: groupId,
          groups: s.groups.map((g) => (g.id === groupId ? { ...g, activeTabId: tabId } : g)),
        }));
      },

      setActiveGroup: (groupId) => set({ activeGroupId: groupId }),

      moveTab: (fromGroup, tabId, toGroup, toIndex) => {
        set((s) => {
          const from = s.groups.find((g) => g.id === fromGroup);
          const tab = from?.tabs.find((t) => t.id === tabId);
          if (!from || !tab) return s;
          let groups = s.groups.map((g) => {
            if (g.id === fromGroup) {
              const tabs = g.tabs.filter((t) => t.id !== tabId);
              return {
                ...g,
                tabs,
                activeTabId: g.activeTabId === tabId ? (tabs[0]?.id ?? null) : g.activeTabId,
              };
            }
            return g;
          });
          groups = groups.map((g) => {
            if (g.id !== toGroup) return g;
            const tabs = [...g.tabs];
            tabs.splice(Math.min(toIndex, tabs.length), 0, tab);
            return { ...g, tabs, activeTabId: tab.id };
          });
          if (groups.length > 1) groups = groups.filter((g) => g.tabs.length > 0);
          return { groups, activeGroupId: toGroup };
        });
      },

      splitRight: (groupId, tabId) => {
        set((s) => {
          const src = s.groups.find((g) => g.id === groupId);
          const tab = src?.tabs.find((t) => t.id === tabId);
          if (!src || !tab) return s;
          const newTab: Tab = { id: newId(), itemId: tab.itemId };
          const group: TabGroup = { id: newId(), tabs: [newTab], activeTabId: newTab.id };
          const idx = s.groups.findIndex((g) => g.id === groupId);
          const groups = [...s.groups];
          groups.splice(idx + 1, 0, group);
          return { groups, activeGroupId: group.id };
        });
      },

      activeTab: () => {
        const s = get();
        const group = s.groups.find((g) => g.id === s.activeGroupId) ?? s.groups[0];
        if (!group) return null;
        const tab = group.tabs.find((t) => t.id === group.activeTabId);
        return tab ? { group, tab } : null;
      },

      cycleTab: (dir) => {
        set((s) => {
          const group = s.groups.find((g) => g.id === s.activeGroupId);
          if (!group || group.tabs.length < 2) return s;
          const idx = group.tabs.findIndex((t) => t.id === group.activeTabId);
          const next = group.tabs[(idx + dir + group.tabs.length) % group.tabs.length]!;
          return {
            groups: s.groups.map((g) => (g.id === group.id ? { ...g, activeTabId: next.id } : g)),
          };
        });
      },
    }),
    { name: "hn.tabs" },
  ),
);
